import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Save } from "lucide-react";
import { getSettings, updateSettings } from "../lib/api";

const TagsInput = ({ label, value, onChange, testid, placeholder }) => {
    const [input, setInput] = useState("");
    const add = () => {
        const t = input.trim();
        if (!t) return;
        onChange([...(value || []), t]);
        setInput("");
    };
    const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
    return (
        <div className="space-y-2">
            <label className="d8-label block">{label}</label>
            <div className="flex gap-2">
                <input
                    data-testid={`${testid}-input`}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
                    placeholder={placeholder}
                    className="flex-1 bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm p-3 focus:outline-none focus:border-[#ff2a2a]"
                />
                <button
                    data-testid={`${testid}-add`}
                    onClick={add}
                    className="border border-[#262626] hover:border-[#ff2a2a] text-[#f5f5f0] font-mono-d8 text-xs uppercase tracking-wider px-4"
                >
                    Add
                </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
                {(value || []).map((t, i) => (
                    <span
                        key={i}
                        data-testid={`${testid}-tag-${i}`}
                        className="inline-flex items-center gap-2 border border-[#262626] font-mono-d8 text-xs text-[#f5f5f0] px-2 py-1"
                    >
                        {t}
                        <button
                            onClick={() => remove(i)}
                            className="text-[#8a8a8a] hover:text-[#ff2a2a]"
                        >
                            ✕
                        </button>
                    </span>
                ))}
            </div>
        </div>
    );
};

export const Settings = () => {
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getSettings().then(setS);
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            const updated = await updateSettings(s);
            setS(updated);
            toast.success("Settings saved");
        } catch (e) {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (!s) return <div className="p-12 d8-label">Loading…</div>;
    const set = (k, v) => setS({ ...s, [k]: v });

    return (
        <div data-testid="settings-page" className="min-h-screen">
            <Toaster theme="dark" position="top-right" />
            <section className="border-b border-[#262626] px-6 md:px-12 py-10 noise-bg">
                <div className="d8-label mb-3">/ Settings</div>
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-5xl leading-none">
                    Control Surface
                </h1>
            </section>

            <section className="p-6 md:p-12 max-w-3xl space-y-8">
                <div className="space-y-2">
                    <label className="d8-label">Brand voice</label>
                    <textarea
                        data-testid="brand-voice"
                        rows={3}
                        value={s.brand_voice}
                        onChange={(e) => set("brand_voice", e.target.value)}
                        className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm p-3 focus:outline-none focus:border-[#ff2a2a]"
                    />
                </div>

                <TagsInput
                    label="Banned topics"
                    testid="banned-topics"
                    placeholder="e.g. specific_public_figure"
                    value={s.banned_topics}
                    onChange={(v) => set("banned_topics", v)}
                />

                <TagsInput
                    label="Restricted terms"
                    testid="restricted-terms"
                    placeholder="e.g. competitor_brand"
                    value={s.restricted_terms}
                    onChange={(v) => set("restricted_terms", v)}
                />

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                        <label className="d8-label">Default platform</label>
                        <select
                            data-testid="default-platform"
                            value={s.default_platform}
                            onChange={(e) => set("default_platform", e.target.value)}
                            className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm p-3"
                        >
                            {["tiktok", "reels", "shorts"].map((p) => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="d8-label">Default darkness</label>
                        <input
                            data-testid="default-darkness"
                            type="number"
                            min={1}
                            max={5}
                            value={s.default_darkness}
                            onChange={(e) => set("default_darkness", Number(e.target.value))}
                            className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm p-3"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="d8-label">Safety threshold (0-10)</label>
                        <input
                            data-testid="safety-threshold"
                            type="number"
                            min={1}
                            max={10}
                            value={s.safety_threshold}
                            onChange={(e) => set("safety_threshold", Number(e.target.value))}
                            className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm p-3"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[#262626] pt-6">
                    <div className="space-y-2">
                        <label className="d8-label">LLM Provider</label>
                        <select
                            data-testid="llm-provider"
                            value={s.llm_provider}
                            onChange={(e) => set("llm_provider", e.target.value)}
                            className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm p-3"
                        >
                            {["anthropic", "openai", "gemini"].map((p) => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="d8-label">LLM Model</label>
                        <input
                            data-testid="llm-model"
                            value={s.llm_model}
                            onChange={(e) => set("llm_model", e.target.value)}
                            className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm p-3"
                        />
                    </div>
                </div>
                <p className="font-mono-d8 text-[11px] text-[#8a8a8a]">
                    Note: backend uses env vars LLM_PROVIDER / LLM_MODEL for actual generation. These fields are stored
                    for reference and future team-config export.
                </p>

                <button
                    data-testid="save-settings"
                    onClick={save}
                    disabled={saving}
                    className="inline-flex items-center gap-2 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-widest px-6 py-3 disabled:opacity-60"
                >
                    <Save className="w-4 h-4" />
                    {saving ? "Saving…" : "Save settings"}
                </button>
            </section>
        </div>
    );
};

export default Settings;
